import React, { useEffect } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import Modal from 'react-bootstrap/Modal';

import { ButtonPrimary, Checkbox, CustomDatePicker, ModalClose } from '../../../common/components';
import FormElement from '../../../common/components/FormElement';
import * as colors from '../../../common/styles/colors';
import * as variables from '../../../common/styles/variables';
import ReactSelect from 'react-select';
import { newMessageStyles, newMessageStylesError } from './styles/NewMessageModalStyles';
import { DATE_FORMAT, REVERSED_DATE_FORMAT_HYPHEN } from '../../../common/constants';
import moment from 'moment';
import { updateMessage } from '../../../common/api/adminMessagesApi';
import { useMsal } from '@azure/msal-react';
import { useNewMessageModalValidation } from './useNewMessageModalValidation';
import { onlyLettersNumbersAndCommas } from '../../utils/text/text';

type UpdateMessageModalProps = {
  show: boolean;
  onHide: Function;
  updateRow?: any;
};

const defaultProps = {
  show: true,
  updateRow: '',
};

interface IOption {
  value: string;
  label: string;
}

const userTypeOptions: Array<IOption> = [
  { value: 'ALL', label: 'All' },
  { value: 'AllCustomers', label: 'All customers' },
  { value: 'AllTPIUsers', label: 'All TPI users' },
  { value: 'SpecificCustomers', label: 'Specific customer(s)' },
  { value: 'SpecificTPIUsers', label: 'Specific TPI user(s)' },
];
const priorityOptions: Array<IOption> = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];
const baseInternalPageOptions: Array<IOption> = [
  { value: '/home/messages', label: 'Messages' },
  { value: '/home/site-details', label: 'Site details' },
  { value: '/home/contract-summary', label: 'Contract summary' },
  { value: '/home/moving-premises', label: 'Moving premises' },
  { value: '/home/mpan-details', label: 'MPAN details' },
  { value: '/home/energy-usage/previous-meter-readings', label: 'Previous meter readings' },
  { value: '/home/meter-reads', label: 'Meter reads' },
  { value: '/home/invoices', label: 'Invoices' },
  { value: '/home/make-a-payment', label: 'Make a payment' },
  { value: '/home/energy-usage/data', label: 'Data' },
  { value: '/home/exclusive-offers', label: 'Exclusive offers' },
  { value: '/home/energy-usage/downloadhhdata', label: 'Download HH data' },
  { value: '/home/energy-usage/smartmeterconsent', label: 'Smart meter consent' },
];
const tpiOnlyInternalPageOptions: Array<IOption> = [
  { value: '/home/loa', label: 'Letter of Authority data' },
  { value: '/home/broker-tpi', label: 'Broker/TPI data' },
];
const statusOptions: Array<IOption> = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Ready', label: 'Ready to publish' },
  { value: 'Published', label: 'Published' },
];

export const UpdateMessageWrapper = styled.div`
  .dropzonecontainer {
    border: 2px solid #dde4e9;
    background-color: #f2f2f2;
    border-radius: 8px;
    min-height: 150px;
    overflow-wrap: break-word;

    div {
      color: ${colors.darkMidGrey};
      font-weight: 600;
    }

    .title {
      font-size: 20px;
    }
  }

  .container {
    border: 1px solid red;
    height: 300px;
    display: flex;
    flex-direction: row;

    flex-wrap: wrap;
  }

  .item {
    border: 1px dashed blue;
    height: 50px;
  }

  .modal-title {
    text-decoration: underline;
    color: ${colors.hydroBlue};
  }

  .dropdown {
    margin-bottom: 20px;
    width: 20%;
    height: 54px;
  }

  .dropdown-third {
    margin-bottom: 20px;
    width: 30%;
    height: 54px;
  }

  .reference-box {
    width: 65%;
  }

  .reference-box-input {
    height: 50px;
    padding-top: 10px;
    font-weight: normal;
  }

  .label {
    display: block;
    margin: 0 0 5px 12px;
    font-size: 16px;
    line-height: 19px;
    color: var(--color-mid-gray);
  }

  .dropdown-full {
    margin-bottom: 20px;
  }

  .upload-btn {
    border-radius: 16px;
  }

  .date-picker {
    width: 30%;
    max-height: 32px;
  }

  .has-error {
    input {
      border: 2px solid #d22f19;
      border-radius: 8px;
    }
  }

  .error-text {
    color: rgb(210, 47, 25);
    font-size: ${variables.fontSizeMedium};
    margin-top: ${variables.spacingXSmall};
    font-weight: bold;
  }

  .smallFont {
    font-size: 14.5px;
  }
`;

export const UpdateMessageModal = (props: UpdateMessageModalProps) => {
  const [subject, setSubject] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [customerRefStr, setCustomerRefStr] = useState<string>('');
  const [customerRefDisabled, setCustomerRefDisabled] = useState<boolean>(true);
  const [customerRefError, setCustomerRefError] = useState<string>('');
  const [customerRefPlaceholder, setCustomerRefPlaceholder] = useState<string>('');
  const [userType, setUserType] = useState<IOption>({ value: '', label: '' });
  const [priority, setPriority] = useState<IOption>({ value: '', label: '' });
  const [startDate, setStartDate] = useState<any>('');
  const [endDate, setEndDate] = useState<any>('');
  const [preference, setPreference] = useState('Internal page');
  const [internalLink, setInternalLink] = useState<IOption>({ value: 'Select', label: 'Select a page' });
  const [externalLink, setExternalLink] = useState<string>('');
  const [selectPreview, setSelectPreview] = useState<boolean>(false);
  const [selectSendEmail, setSelectSendEmail] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('Draft');
  const { instance, accounts } = useMsal();
  const [showInMessages, setShowInMessages] = useState<boolean>(false);
  const [internalPageOptions, setInternalPageOptions] = useState<Array<IOption>>(baseInternalPageOptions);

  let startDateWindowMin = moment().format(REVERSED_DATE_FORMAT_HYPHEN);
  let customerRefArr: Array<string> = [];

  const {
    subjectError,
    messageError,
    userTypeError,
    priorityError,
    startDateError,
    endDateError,
    internalLinkError,
    externalLinkError,
    validateSubject,
    validateMessage,
    validateUserType,
    validatePriority,
    validateStartDate,
    validateEndDate,
    validateInternalLink,
    validateExternalLink,
    clearAllValidation,
  } = useNewMessageModalValidation();

  useEffect(() => {
    handleFormRefresh();
  }, [props.updateRow]);

  useEffect(() => {
    if (userType.value === 'AllTPIUsers' || userType.value === 'SpecificTPIUsers') {
      setInternalPageOptions([...baseInternalPageOptions, ...tpiOnlyInternalPageOptions]);
    } else {
      setInternalPageOptions(baseInternalPageOptions);
    }
  }, [userType]);

  const handleFormRefresh = () => {
    let updateMessage = JSON.parse(props.updateRow.json);
    let updateStartDate = moment(props.updateRow.startDate);
    let updateEndDate = moment(props.updateRow.endDate);

    setSubject(props.updateRow.subject);
    setMessage(updateMessage.message);
    handleUserTypeCheck();
    setPriority({ value: props.updateRow.priority, label: props.updateRow.priority });
    setStartDate(updateStartDate);
    setEndDate(updateEndDate);
    handlePreferenceCheck();
    handleStatusCheck();
    setSelectPreview(false);
    setCustomerRefPlaceholder('');
    handleCustomerRefEnabledCheck();
    setCustomerRefStr(props.updateRow.references.toString());
    setCustomerRefError('');
    customerRefArr = [];
    setSelectSendEmail(props.updateRow.sendEmail);
    setShowInMessages(props.updateRow.showInMessages);

    clearAllValidation();
  };

  const handlePreferenceCheck = async () => {
    if (props.updateRow) {
      if (props.updateRow.internalLink.length) {
        setPreference('Internal page');
        handleInternalPageCheck();
      } else if (props.updateRow.externalLink.length) {
        setPreference('External page');
        setExternalLink(props.updateRow.externalLink);
      } else {
        setPreference('No link');
      }
    }
  };

  const handleInternalPageCheck = async () => {
    function findInternalLink(link: { value: any }) {
      return link.value === props.updateRow.internalLink;
    }
    const updateLink = baseInternalPageOptions.find(findInternalLink)?.label;
    if (updateLink) {
      setInternalLink({ value: props.updateRow.internalLink, label: updateLink });
    }
  };

  const handleStatusCheck = async () => {
    function findStatus(status: { value: any }) {
      return status.value === props.updateRow.status;
    }
    const updateStatus = statusOptions.find(findStatus)?.label;
    if (updateStatus) {
      setStatus(updateStatus);
    }
  };

  const handleUserTypeCheck = async () => {
    function findUserType(usertype: { value: any }) {
      return usertype.value === props.updateRow.userType;
    }
    const updatedUserType = userTypeOptions.find(findUserType)?.label;
    if (updatedUserType) {
      setUserType({ value: props.updateRow.userType, label: updatedUserType });
    }
  };

  const handleCustomerRefEnabledCheck = async () => {
    if (props.updateRow.userType === 'SpecificCustomers' || props.updateRow.userType === 'SpecificTPIUsers') {
      setCustomerRefDisabled(false);
    } else {
      setCustomerRefDisabled(true);
    }
  };

  const handleUserTypeChange = async (userType: IOption) => {
    setUserType(userType);
    setCustomerRefStr('');
    setSelectSendEmail(false);

    if (userType.value === 'SpecificCustomers') {
      setCustomerRefDisabled(false);
      setCustomerRefPlaceholder('Input Customer account references(s) separated by a comma');
    } else if (userType.value === 'SpecificTPIUsers') {
      setCustomerRefDisabled(false);
      setCustomerRefPlaceholder('Input TPI references(s) separated by a comma');
    } else {
      setCustomerRefError('');
      setCustomerRefDisabled(true);
      setCustomerRefPlaceholder('');
    }
  };

  const handleMessagePriorityChange = async (messagePriority: IOption) => {
    setPriority(messagePriority);
  };

  const handleInternalPageChange = async (internalPage: IOption) => {
    setInternalLink(internalPage);
    setExternalLink('');
  };

  const handleSelectPreview = () => {
    setSelectPreview(!selectPreview);
  };

  const handleShowInMessages = () => {
    setShowInMessages(!showInMessages);
  };

  const handleSelectSendEmail = () => {
    setSelectSendEmail(!selectSendEmail);
  };

  const validateLink = () => {
    if (preference === 'Internal page') {
      if (internalLink.value !== 'Select' && internalLink.value !== '') {
        return true;
      } else {
        return false;
      }
    } else if (preference === 'External page') {
      if (externalLink.length) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  };

  const validateReferenceInput = () => {
    setCustomerRefError('');

    if (userType.value === 'SpecificCustomers') {
      return validateSpecificCustomers(customerRefStr);
    } else if (userType.value === 'SpecificTPIUsers') {
      return validateTpiCustomers(customerRefStr);
    } else {
      return true;
    }
  };

  const validateSpecificCustomers = (references: string) => {
    if (!references.length) {
      setCustomerRefError('Input Customer account references(s) separated by a comma');
      return false;
    } else {
      const referenceArray = references.split(/[ ,]+/);
      for (let i = 0; i < referenceArray.length; i++) {
        if (referenceArray[i].toUpperCase().startsWith('TPI')) {
          setCustomerRefError('Input Customer account references(s) separated by a comma');
          return false;
        }
      }
      customerRefArr = referenceArray;
    }
    return true;
  };

  const validateTpiCustomers = (references: string) => {
    if (!references.length) {
      setCustomerRefError('Input TPI references(s) separated by a comma');
      return false;
    } else {
      const referenceArray = references.split(/[ ,]+/);
      for (let i = 0; i < referenceArray.length; i++) {
        if (!referenceArray[i].toUpperCase().startsWith('TPI')) {
          setCustomerRefError('Input TPI references(s) separated by a comma');
          return false;
        }
      }
      customerRefArr = referenceArray;
    }
    return true;
  };

  const handleCustomerRefChange = (event: any) => {
    setCustomerRefError('');
    if (onlyLettersNumbersAndCommas(event.target.value)) {
      setCustomerRefStr(event.target.value);
    }
  };

  const checkMaxStartDate = () => {
    const startDateWindowMax = moment(startDateWindowMin).add(6, 'M').endOf('month');
    if (endDate && endDate <= startDateWindowMax) {
      return endDate.format(REVERSED_DATE_FORMAT_HYPHEN);
    } else {
      return startDateWindowMax.format(REVERSED_DATE_FORMAT_HYPHEN);
    }
  };

  const adminMessageUpdateSuccess = (result: Common.IResult<any>) => {
    if (result && result.success) {
      props.onHide();
    } else {
      error('updatebulkmessage');
    }
  };

  const error = (error: string) => {
    const message = `API error calling: ${error}`;
    console.error(message);
    props.onHide();
  };

  const handleSaveClick = async (event: any) => {
    event.preventDefault();
    let json = { message: message };
    let sentEmailTemp = selectSendEmail;

    if (status === 'Published') {
      sentEmailTemp = false;
    }

    clearAllValidation();
    validateSubject(subject);
    validateMessage(json.message);
    validateUserType(userType.value);
    validatePriority(priority.value);
    {
      preference === 'Internal page' && validateInternalLink(internalLink.value);
    }
    {
      preference === 'External page' && validateExternalLink(externalLink);
    }
    validateStartDate(startDate);
    validateEndDate(endDate);
    if (subject && !subjectError) {
      if (json.message && !messageError) {
        if (userType.value && !userTypeError) {
          if (priority.value && !priorityError) {
            if (validateLink()) {
              if (!customerRefError && validateReferenceInput()) {
                if (startDate && !startDateError) {
                  if (endDate && !endDateError) {
                    const data: Api.IAdminCreateMessageData = {
                      id: props.updateRow.id,
                      subject: subject,
                      startDate: startDate ? startDate.format(REVERSED_DATE_FORMAT_HYPHEN) : '',
                      endDate: endDate ? endDate.format(REVERSED_DATE_FORMAT_HYPHEN) : '',
                      priority: priority.value,
                      externalLink: preference === 'External page' ? externalLink : '',
                      internalLink: preference === 'Internal page' ? internalLink.value : '',
                      showInMessages: showInMessages,
                      status: status === 'Ready to publish' ? 'Ready' : 'Draft',
                      json: JSON.stringify(json),
                      userType: userType.value,
                      references: customerRefArr,
                      sendEmail: sentEmailTemp,
                    };

                    await updateMessage(accounts[0], data, instance, adminMessageUpdateSuccess, error);
                  }
                }
              }
            }
          }
        }
      }
    }
  };

  const handleCancelClick = () => {
    handleFormRefresh();
    props.onHide();
  };

  return (
    <>
      <Modal animation={false} className={'custom-modal'} show={props.show} size={'lg'} backdrop='static' centered>
        <UpdateMessageWrapper>
          <Modal.Header className='pt-0 pb-0 pr-0'>
            <Modal.Title className='modal-title'>Update message</Modal.Title>
            <ModalClose onClick={() => handleCancelClick()} />
          </Modal.Header>

          <Modal.Body>
            <form>
              <div className='pt-2'>
                <div className='label'>Subject *</div>
                <FormElement.Textarea
                  id='subject'
                  placeholder={'Max characters 150'}
                  className='c-form__input no-resize'
                  name='subject'
                  value={subject}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setSubject(e.target.value);
                  }}
                  rows={2}
                  maxLength={150}
                  required={true}
                  errorMessage={subjectError}
                />
              </div>

              <div className='pt-1'>
                <div className='label'>Message *</div>
                <FormElement.Textarea
                  id='message'
                  placeholder={''}
                  className='c-form__input no-resize'
                  name='message'
                  value={message}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setMessage(e.target.value);
                  }}
                  rows={3}
                  required={true}
                  errorMessage={messageError}
                />
              </div>

              <div className='container-fluid'>
                <div className='row justify-content-between'>
                  <div className='label'>
                    Select a message type or input specific customer account reference(s) or TPI reference(s)
                  </div>
                  <div className='pt-1 dropdown-third'>
                    <ReactSelect
                      autoFocus={false}
                      blurInputOnSelect={true}
                      placeholder={''}
                      value={userType}
                      styles={userTypeError ? newMessageStylesError : newMessageStyles}
                      options={userTypeOptions}
                      onChange={handleUserTypeChange}
                    />
                    {userTypeError && <div className='error-text'>Required field</div>}
                  </div>

                  <div className='reference-box'>
                    <FormElement.Textarea
                      id='specRef'
                      placeholder={customerRefPlaceholder}
                      className='c-form__input no-resize reference-box-input'
                      label={''}
                      name='specRef'
                      value={customerRefStr}
                      onChange={e => handleCustomerRefChange(e)}
                      rows={1}
                      errorMessage={customerRefError}
                      disabled={customerRefDisabled}
                    />
                  </div>
                </div>
              </div>

              {(userType.value === 'SpecificCustomers' || userType.value === 'SpecificTPIUsers') && (
                <div className='smallFont pb-3'>
                  <Checkbox
                    id={'select-push-email'}
                    label={
                      'Do you want to send a push email out to the users to inform them of an important message on the portal?'
                    }
                    onChange={handleSelectSendEmail}
                    checked={selectSendEmail}
                  ></Checkbox>
                </div>
              )}

              <div className='container-fluid'>
                <div className='row justify-content-between pb-4'>
                  <div className='pt-1 dropdown-third'>
                    <div className='label'>Priority *</div>
                    <ReactSelect
                      autoFocus={false}
                      blurInputOnSelect={true}
                      placeholder={''}
                      value={priority}
                      styles={priorityError ? newMessageStylesError : newMessageStyles}
                      options={priorityOptions}
                      onChange={handleMessagePriorityChange}
                    />
                    {priorityError && <div className='error-text'>Required field</div>}
                  </div>

                  <div className='pt-1 date-picker'>
                    <div className='label'>Start date *</div>
                    <div className={startDateError ? 'has-error' : ''}>
                      <CustomDatePicker
                        date={startDate ? startDate.format(DATE_FORMAT) : ''}
                        showIcon={true}
                        minDate={startDateWindowMin}
                        maxDate={checkMaxStartDate()}
                        onChange={(e: any) => {
                          setStartDate(e);
                        }}
                      />
                    </div>
                    {startDateError && <div className='error-text'>Required field</div>}
                  </div>

                  <div className='pt-1 date-picker'>
                    <div className='label'>End date *</div>
                    <div className={endDateError ? 'has-error' : ''}>
                      <CustomDatePicker
                        className=''
                        date={endDate ? endDate.format(DATE_FORMAT) : ''}
                        showIcon={true}
                        minDate={startDate && startDate.format(REVERSED_DATE_FORMAT_HYPHEN)}
                        maxDate={''}
                        onChange={(e: any) => {
                          setEndDate(e);
                        }}
                        disabled={startDate === ''}
                      />
                    </div>
                    {endDateError && <div className='error-text'>Required field</div>}
                  </div>
                </div>
              </div>

              <div
                className={
                  userTypeError || priorityError || startDateError || endDateError
                    ? 'smallFont py-4 '
                    : 'smallFont pt-2 pb-3'
                }
              >
                <Checkbox
                  id={'select-msg-grid-notify'}
                  label={'Do you want to notify the users via the message grid that this message is available?'}
                  onChange={handleShowInMessages}
                  checked={showInMessages}
                ></Checkbox>
              </div>

              <FormElement.Radio
                id='page-link-preference'
                label={'Where would you like the more info links to navigate?'}
                name='linkPref'
                elements={['Internal page', 'External page', 'No link']}
                value={preference}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPreference(e.target.value);
                }}
              />

              {preference === 'Internal page' && (
                <div className='dropdown-full'>
                  <ReactSelect
                    autoFocus={false}
                    blurInputOnSelect={true}
                    placeholder={'Select a page'}
                    value={internalLink}
                    styles={internalLinkError ? newMessageStylesError : newMessageStyles}
                    options={internalPageOptions}
                    onChange={handleInternalPageChange}
                  />
                  {internalLinkError && <div className='error-text'>Select a page</div>}
                </div>
              )}

              {preference === 'External page' && (
                <div className=''>
                  <FormElement.Textarea
                    id='externalPage'
                    placeholder={'Add external page URL'}
                    className='c-form__input no-resize'
                    label={''}
                    name='externalPage'
                    value={externalLink}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      setExternalLink(e.target.value);
                      setInternalLink({ value: 'Select', label: 'Select a page' });
                    }}
                    rows={1}
                    maxLength={150}
                    required={false}
                    errorMessage={externalLinkError}
                  />
                </div>
              )}

              <div className='pt-1 mb-2'>
                <Checkbox
                  id={'select-preview'}
                  label={'Select checkbox to view preview of the message in message box.'}
                  onChange={handleSelectPreview}
                  checked={selectPreview}
                ></Checkbox>
              </div>

              <div className='pb-3'>
                <section className='dropzonecontainer'>
                  {!selectPreview && (
                    <div className='p-2' style={{ fontStyle: 'italic' }}>
                      Preview of the message
                    </div>
                  )}
                  {selectPreview && (
                    <div className='p-2'>
                      {message} &nbsp;
                      {(preference === 'Internal page' || preference === 'External page') && (
                        <a href={''} style={{ color: '#216EF2' }}>
                          More info
                        </a>
                      )}
                    </div>
                  )}
                </section>
              </div>

              {props.updateRow.status !== 'Published' && (
                <FormElement.Radio
                  id='page-status'
                  className='c-form__input'
                  label={'Status'}
                  name='page-status'
                  elements={['Ready to publish', 'Draft']}
                  value={status}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setStatus(e.target.value);
                  }}
                />
              )}

              {props.updateRow.status === 'Published' && (
                <div className='pt-2 pb-3'>
                  Status:{' '}
                  <b>
                    <i>Published</i>
                  </b>
                </div>
              )}
            </form>
          </Modal.Body>

          <div className='float-right'>
            <ButtonPrimary
              disabled={false}
              className='mt-3 upload-btn'
              title={props.updateRow.status === 'Published' ? 'Unpublish' : 'Save'}
              onClick={handleSaveClick}
            />

            <ButtonPrimary
              disabled={false}
              className='mt-3 ml-3 upload-btn'
              title='Cancel'
              onClick={event => {
                event.preventDefault();
                handleCancelClick();
              }}
            />
          </div>
        </UpdateMessageWrapper>
      </Modal>
    </>
  );
};

UpdateMessageModal.defaultProps = defaultProps;
